import * as React from 'react';
import { v4 } from 'uuid';
import Styled, { css } from 'styled-components';

import { ActionButton } from '../ActionButton';
import SVGIcon, { VALID_ICONS } from '../Icon/SVGIcon';

interface IProps {
  visible: boolean;
  maxWidth?: string;
}

const Input = Styled.input<IProps>`
  width: 100%;
  position: relative;
  outline: none;
  border: none;
  background: none;
  font-size: 1.4rem;
  padding: 0.25em 0 0 0.5em;

  &::placeholder {
    color: ${({ theme: { input } }) => input.colorPlaceholder};
  }

  ${({ visible }) => !visible && `
    pointer-events: none;
  `}
`;

const InputWrapper = Styled(ActionButton)<IProps>`
  padding: 0.25em 1.125em;
  overflow: hidden;
  transition: max-width 100ms ease;
  max-width: 3.75em;
  justify-content: initial;

  ${({ visible, maxWidth }) => !!visible && `
    width: 100%;
    max-width: ${maxWidth || '17.5em'};
  `}

  ${({ active, theme: { actionButton } }) => active && `
    color: ${actionButton.colorText};
    background: unset;
    cursor: default;

    svg {
      fill: ${actionButton.colorText};
    }
  `}

  &:active {
    color: ${({ theme: { actionButton } }) => actionButton.colorText};
    background: unset;

    svg {
      fill: ${({ theme: { actionButton } }) => actionButton.colorText};
    }
  }
`;

const SearchIcon = Styled(SVGIcon)<{ setIconRight?: boolean; iconClickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  order: -1;

  ${({ setIconRight }) => !!setIconRight && `
    order: 2;
  `}

  ${({ iconClickable, theme }) => !!iconClickable && css`
    cursor: pointer;

    &:hover {
      color: ${theme.link.colorLinkHover};
    }
  `}

  /* stylelint-disable no-descending-specificity */
  svg {
    margin-right: 0;
    width: 1.5em;
    height: 1.5em;
  }
  /* stylelint-enable */
`;

const CloseButton = Styled(SVGIcon)`
  display: flex;
  align-items: center;
  justify-content: center;

  /* stylelint-disable no-descending-specificity */
  svg {
    margin-right: 0;
    width: 0.75em;
    height: 0.75em;
  }
  /* stylelint-enable */
`;

const CloseButtonContain = Styled.div<IProps>`
  padding: 0.375em 0.25em;
  cursor: pointer;

  ${({ visible }) => !visible && `
    display: none;
  `}

  &:hover svg {
    fill: ${({ theme }) => theme.colorSlate70};
  }

  &:focus {
    outline: ${({ theme }) => theme.outline};
  }
`;

export interface ISearchButtonProps {
  icon: VALID_ICONS;
  maxWidth?: string;
  initialValue?: string;
  placeholder?: string;
  forceActive?: boolean;
  setIconRight?: boolean;
  inputChangeHandler?: React.FormEventHandler<HTMLInputElement>;
  closeHandler? (e?: Event): void;
  submitHandler? (e?: Event): void;
}

interface ISearchButtonState {
  inputVisible: boolean;
  inputValue: string;
}

export class SearchButton extends React.Component<ISearchButtonProps, ISearchButtonState> {
  public inputRef: React.RefObject<HTMLInputElement>;
  public searchBarRef: React.RefObject<HTMLButtonElement>;

  public state = {
    inputVisible: false,
    inputValue: ''
  };

  private readonly _id: string = v4();

  constructor (props: ISearchButtonProps) {
    super(props);
    this.inputRef = React.createRef();
    this.searchBarRef = React.createRef();

    this.state = {
      inputValue: this.props.initialValue || '',
      inputVisible: !!this.props.initialValue
    };
  }

  public componentDidMount () {
    // allow closing search by hitting the 'Escape' key
    document.addEventListener('keydown', this.keyListener);
    // allow closing search by clicking outside of the input field
    document.addEventListener('click', this.handleClick, false);
  }

  public componentWillUnmount () {
    document.removeEventListener('keydown', this.keyListener);
    document.removeEventListener('click', this.handleClick, false);
  }

  public render (): JSX.Element {
    const controlsTabIndex: number = this.state.inputVisible ? 0 : -1;

    return (
      <InputWrapper
        {...this.props}
        // @ts-ignore
        as='div'
        aria-controls={this._id}
        aria-label='Search'
        title='Search'
        onClick={this.showInput}
        onKeyPress={this.handleEnterOpen}
        visible={!!this.props.forceActive || this.state.inputVisible}
        active={this.state.inputVisible}
        tabIndex={this.state.inputVisible ? -1 : 0}
        ref={this.searchBarRef}
        aria-expanded={this.state.inputVisible}
        role='button'
      >
        <Input
          id={this._id}
          aria-label='Search'
          onChange={this.changeInput}
          ref={this.inputRef}
          visible={this.state.inputVisible}
          value={this.state.inputValue}
          tabIndex={controlsTabIndex}
          placeholder={this.props.placeholder}
        />
        {!this.props.forceActive && (
          <CloseButtonContain
            role='button'
            aria-label='Clear'
            title='Clear'
            visible={this.state.inputVisible}
            onClick={this.handleClose}
            onKeyPress={this.handleEnterClose}
            tabIndex={controlsTabIndex}
          >
            <CloseButton
              icon='close'
            />
          </CloseButtonContain>
        )}
        <SearchIcon
          icon={this.props.icon}
          onClick={this.handleIconClick}
          setIconRight={this.props.setIconRight}
          iconClickable={!!this.props.submitHandler && this.state.inputVisible}
        />
      </InputWrapper>
    );
  }

  private readonly keyListener: (event: KeyboardEvent) => void = e => {
    if (
      !this.inputRef.current ||
      !document.activeElement ||
      this.inputRef.current.id !== document.activeElement.id
    ) {
      return;
    }
    if (e.key === 'Escape') {
      this.hideInput(e);
    } else if (this.props.submitHandler && e.key === 'Enter') {
      this.props.submitHandler(e);
    }
  }

  private readonly changeInput: React.FormEventHandler<HTMLInputElement> = e => {
    e.preventDefault();

    const inputValue = e.currentTarget.value;
    this.setState({ inputValue });

    if (this.props.inputChangeHandler) {
      this.props.inputChangeHandler(e);
    }
  }

  private readonly showInput: React.MouseEventHandler = () => {
    this.setState({ inputVisible: true });
    if (this.inputRef.current) {
      this.inputRef.current.focus();
    }
  }

  private readonly handleIconClick: React.MouseEventHandler = () => {
    if (!!this.props.submitHandler && this.state.inputVisible) {
      this.props.submitHandler();
    }
  }

  private readonly handleClose: React.MouseEventHandler = e => {
    // tslint:disable-next-line: no-any
    this.hideInput(e as any);
  }

  private readonly handleEnterClose: React.KeyboardEventHandler<HTMLDivElement> = e => {
    const ENTER_KEYCODE: number = 13;

    if (e.type === 'keypress' && e.key === 'Enter' || e.which === ENTER_KEYCODE) {
      // tslint:disable-next-line: no-any
      this.hideInput(e as any);
    }
  }

  private readonly hideInput: (e: Event) => void = e => {
    if (!!this.props.forceActive) {
      this.setState({ inputVisible: false });

      return;
    }
    if (!this.state.inputVisible) { return; }

    e.stopPropagation();
    this.setState({ inputVisible: false, inputValue: '' });

    if (this.props.closeHandler) {
      this.props.closeHandler(e);
    }
  }

  private readonly handleClick: EventListener = e => {
    if (
      !!this.state.inputValue ||
      !this.searchBarRef.current ||
      !e.target ||
      this.searchBarRef.current.contains(e.target as Node)
    ) {
      return;
    }

    this.hideInput(e);
  }

  private readonly handleEnterOpen: React.KeyboardEventHandler<HTMLButtonElement> = e => {
    const ENTER_KEYCODE: number = 13;

    if (e.type === 'keypress' && e.key === 'Enter' || e.which === ENTER_KEYCODE) {
      // tslint:disable-next-line: no-any
      this.showInput(e as any);
    }
  }
}
